import React from 'react';
import {
  Sheet,
  SheetContent,
} from '@/components/ui/sheet';
import {
  Bot,
  Megaphone,
  ChevronRight,
  Cloud,
  History,
  Info,
  Rocket,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface ProjectSettingsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOptionSelect: (
    section: 'info' | 'deploy' | 'optidev-cloud' | 'agent-settings' | 'reports',
  ) => void;
  onVersionHistoryClick?: () => void;
}

interface OptionItem {
  id:
    | 'info'
    | 'deploy'
    | 'optidev-cloud'
    | 'agent-settings'
    | 'reports'
    | 'version-history';
  label: string;
  description: string;
  icon: React.ElementType;
}

const OPTIONS: OptionItem[] = [
  {
    id: 'version-history',
    label: 'Version History',
    description: 'View and restore previous versions',
    icon: History,
  },
  {
    id: 'info',
    label: 'Project Settings',
    description: 'Name, domain, and configuration',
    icon: Info,
  },
  {
    id: 'deploy',
    label: 'Deploy History',
    description: 'View builds and deployments',
    icon: Rocket,
  },
  {
    id: 'optidev-cloud',
    label: 'OptiDev Cloud',
    description: 'Database, auth, and storage',
    icon: Cloud,
  },
  {
    id: 'agent-settings',
    label: 'Agent Settings',
    description: 'AI model and preferences',
    icon: Bot,
  },
  {
    id: 'reports',
    label: 'Broadcast',
    description: 'Scheduled snapshots to Slack, Teams, or PDF',
    icon: Megaphone,
  },
];

export function ProjectSettingsSheet({
  open,
  onOpenChange,
  onOptionSelect,
  onVersionHistoryClick,
}: ProjectSettingsSheetProps): JSX.Element {
  const handleOptionClick = (optionId: OptionItem['id']) => {
    if (optionId === 'version-history') {
      onVersionHistoryClick?.();
      onOpenChange(false);
    } else {
      onOptionSelect(optionId);
      onOpenChange(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="h-auto p-0 flex flex-col rounded-t-3xl border-0"
        hideCloseButton
      >
        {/* Handle bar */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 rounded-full bg-muted-foreground/30" />
        </div>

        {/* Options list */}
        <div className="px-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))]">
          {OPTIONS.map((option) => {
            const Icon = option.icon;
            return (
              <button
                key={option.id}
                onClick={() => handleOptionClick(option.id)}
                className={cn(
                  "w-full flex items-center gap-4 px-4 py-3.5 rounded-xl",
                  "hover:bg-muted active:bg-muted/80",
                  "transition-colors duration-150",
                  "text-left"
                )}
              >
                <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-foreground">
                    {option.label}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {option.description}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              </button>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}
